import { z, type ZodTypeAny } from 'zod';

export type EntityType = string;

export const HierarchyEntry = z.object({
  key: z.string(),
  label: z.string().min(1)
});
export type HierarchyEntry = z.infer<typeof HierarchyEntry>;

// Cross-field hierarchy invariants live here (not in a test) so they're enforced at registry
// registration for every adapter — see skill-platform-registry.ts `AdapterConfig.parse(...)`.
export const AdapterConfig = z
  .object({
    platformName: z.string().min(1),
    platformShortLabel: z.string().min(1).max(4),
    fullHierarchy: z.array(HierarchyEntry).min(1),
    createSequence: z.array(z.string()).min(1),
    requiredAncestors: z.array(z.string()),
    rootEntityType: z.string().min(1),
    // The entity recorded as the media_buy row. Usually the last created entity, but not always:
    // Meta records the campaign while also creating an ad set beneath it (Snowflake keys Meta buys
    // at the campaign — docs/adr/0004-meta-media-buy-is-the-campaign.md). Entries after it in
    // createSequence are children of the buy.
    mediaBuyEntityType: z.string().min(1),
    // UI metadata: the workspace stepper labels (e.g. ['Configure', 'Review']). Shared so the
    // frontend chat modal can render the step indicator without pulling in the LLM prompt.
    workflowSteps: z.array(z.string().min(1)).min(1)
  })
  .superRefine((config, ctx) => {
    const hierarchyKeys = new Set(config.fullHierarchy.map(e => e.key));

    if (new Set(config.createSequence).size !== config.createSequence.length) {
      ctx.addIssue({ code: 'custom', message: 'createSequence has duplicate entity types' });
    }
    if (!config.createSequence.includes(config.mediaBuyEntityType)) {
      ctx.addIssue({
        code: 'custom',
        message: `mediaBuyEntityType '${config.mediaBuyEntityType}' must be in createSequence`
      });
    }
    if (!hierarchyKeys.has(config.rootEntityType)) {
      ctx.addIssue({
        code: 'custom',
        message: `rootEntityType '${config.rootEntityType}' is not in fullHierarchy`
      });
    }
    for (const entityType of config.createSequence) {
      if (!hierarchyKeys.has(entityType)) {
        ctx.addIssue({
          code: 'custom',
          message: `createSequence entry '${entityType}' is not in fullHierarchy`
        });
      }
    }
    const createdSet = new Set(config.createSequence);
    for (const ancestor of config.requiredAncestors) {
      if (!hierarchyKeys.has(ancestor)) {
        ctx.addIssue({
          code: 'custom',
          message: `requiredAncestor '${ancestor}' is not in fullHierarchy`
        });
      }
      if (createdSet.has(ancestor)) {
        ctx.addIssue({
          code: 'custom',
          message: `'${ancestor}' is in both requiredAncestors and createSequence (must be disjoint)`
        });
      }
    }
  });
export type AdapterConfig = z.infer<typeof AdapterConfig>;

export type AdapterSchemas = {
  entityPayloads: Record<EntityType, ZodTypeAny>;
};

export type AncestryRecord = {
  platformEntityId: string;
  internalId: string;
};

export type AncestryMap = Map<EntityType, AncestryRecord>;

export type CreatedEntity = {
  entityId: string;
  /**
   * Non-fatal problems from follow-up calls that ran after the entity itself was created
   * remotely (e.g. the Google Ads campaign-criteria mutate). The entity exists and must be
   * recorded locally; warnings are threaded to the confirm response for the user to act on.
   */
  warnings?: string[];
};

// The metadata for the media_buy row a concrete create flow derives from its created entities.
export type MediaBuyMetadata = {
  name: string;
  start_date: string;
  end_date: string;
  budget: number;
};

// A remote entity fetched + validated so the confirm flow can persist it locally (used when the user
// references an existing entity created outside media-tool, e.g. a Google Ads campaign made in the UI).
export type HydratedEntity = {
  rawEntity: Record<string, unknown>;
  entityName: string;
};

export type TargetingModule = {
  key: string;
  label: string;
  kind: 'multi-select' | 'tree' | 'free-text';
  optionsSource: 'static' | 'lookup' | 'zoho-derived';
  staticOptions?: Array<{ id: string; label: string }>;
  zohoField?: string;
};

export type PacingOption = 'lifetime' | 'daily' | 'flight' | 'none';

export type AdapterCapabilities = {
  lineItemTypes: string[];
  /**
   * Optional aliases keyed by lineItemType. When the resolver scans free-form text
   * (tactic, channel, Zoho description/notes) it matches both the canonical type
   * names and any aliases listed here. e.g. { banner: ['display'], video: ['ctv'] }.
   */
  lineItemTypeAliases?: Record<string, string[]>;
  /**
   * When true, the configuration card shows a required account-ID field (e.g. the Google Ads
   * customer ID) and the finalize prompt pins its value for the create flow.
   */
  requiresAccountId?: boolean;
  /**
   * Singular noun the configuration card uses for the account-level entity (e.g. 'advertiser' for
   * Beeswax). Only meaningful when `requiresAccountId`. Defaults to 'account'.
   */
  accountLabel?: string;
  /**
   * Placeholder shown in the account-ID field. Defaults to a Google-Ads-style customer id example.
   */
  accountIdPlaceholder?: string;
  biddingStrategies: string[];
  /**
   * Pre-selects the configuration card's bidding strategy. Used when the DSP has one obvious
   * starting strategy (e.g. Google Ads SEM always starts on MAXIMIZE_CLICKS). The user can still
   * pick any of `biddingStrategies`.
   */
  defaultBiddingStrategy?: string;
  /**
   * Omit entirely for DSPs that have no pacing concept (e.g. Google Ads): the configuration card
   * then hides its pacing dropdown. When present and non-empty, the card requires a selection.
   */
  pacingOptions?: PacingOption[];
  budgetTypes: string[];
  targetingModules: TargetingModule[];
  /**
   * When true, the configuration card shows a required CPM bid field (e.g. Beeswax, a genuine CPM
   * DSP). Omit for CPC platforms (e.g. Google Ads) where a CPM bid is meaningless — the card then
   * hides the field and does not gate completion on it. The per-ad-group CPC bid for those platforms
   * is derived server-side from the line item's planned unit cost.
   */
  usesCpmBid?: boolean;
  /**
   * When true, the configuration card offers a campaign-split repeater: the line item can run as more
   * than one DSP campaign (e.g. Google Ads brand vs non-brand), each its own media plan, with the
   * per-campaign budget shares summing to the line-item price. Default is a single campaign.
   */
  supportsCampaignSplit?: boolean;
  /**
   * Objective-split platforms (Meta): the line item always runs as one campaign per row, each row
   * an objective from `lineItemTypes`, instead of a single campaign of one line-item type. The
   * configuration card seeds `defaults` (share as a percent of the line-item price) as a required
   * split, offers `performanceGoals[objective]` on rows whose objective lists any, and drops the
   * sections a per-objective create-only flow has no use for (campaign mode, line-item type,
   * bidding, tracking).
   */
  objectiveSplit?: {
    defaults: Array<{ objective: string; sharePercent: number }>;
    performanceGoals?: Record<string, string[]>;
  };
  /**
   * When true, the configuration card shows a required Page picker (discoverDspPages, with a
   * pasted id as fallback) — Meta's ad identity, pinned next to the ad account.
   */
  requiresPageId?: boolean;
  /**
   * Audience fields on the configuration card: an optional special-ad-category multi-select and a
   * minimum-age field seeded with `defaultAgeMin` and capped at `maxAgeMin` (the same bounds the
   * server's payload schema enforces). Omit for platforms whose card has neither.
   */
  audience?: { specialAdCategories?: string[]; defaultAgeMin?: number; maxAgeMin?: number };
  managerAccounts?: { id: string; label: string }[];
};

// Assumes a linear fullHierarchy (root -> ... -> leaf). DSPs with non-linear
// shapes (e.g. branching insertion orders) will need a different resolution.
export function parentEntityTypeOf(
  config: AdapterConfig,
  entityType: EntityType
): EntityType | null {
  const idx = config.fullHierarchy.findIndex(e => e.key === entityType);
  if (idx <= 0) return null;
  return config.fullHierarchy[idx - 1].key;
}
